import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Linkedin, Mail, ArrowRight } from "lucide-react";
import { useLenis } from "lenis/react";
import { RuotaMark } from "./RuotaMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  { label: "Philosophy", href: "#philosophy", testid: "footer-link-philosophy" },
  { label: "Inspiration", href: "#destinations", testid: "footer-link-destinations" },
  { label: "Our Approach", href: "#philosophy", testid: "footer-link-process" },
  { label: "Begin Here", href: "#enquiry", testid: "footer-link-enquiry" },
];

const BRAND_STYLES = [
  { name: "AMAN", cls: "font-light uppercase tracking-[0.5em] not-italic" },
  { name: "Belmond", cls: "uppercase tracking-[0.35em] not-italic" },
  { name: "Four Seasons", cls: "italic" },
  { name: "ROSEWOOD", cls: "font-light uppercase tracking-[0.45em] not-italic" },
  { name: "six senses", cls: "lowercase italic font-light" },
  { name: "COMO", cls: "font-accent uppercase tracking-[0.4em] not-italic" },
  { name: "Cheval Blanc", cls: "italic font-light" },
  { name: "One&Only", cls: "font-semibold not-italic" },
  { name: "ROCCO FORTE", cls: "uppercase tracking-[0.25em] not-italic" },
  { name: "Relais & Châteaux", cls: "italic" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const lenis = useLenis();

  const go = (e, href) => {
    e.preventDefault();
    lenis?.scrollTo(href, { offset: -72, duration: 1.4 });
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. Keep moving.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe right now. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer data-testid="footer" className="bg-[#0D1F23] pt-24 text-[#F7F0E3] sm:pt-32 print:hidden">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
        <motion.div
          data-testid="footer-brands"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1 }}
        >
          <div className="flex flex-wrap items-center gap-5">
            <span
              data-testid="virtuoso-badge"
              className="rounded-full border-2 border-[#E2725B] px-6 py-2.5 font-display text-lg uppercase tracking-[0.18em] text-[#E2725B]"
            >
              Proud Virtuoso® Member
            </span>
            <p className="font-accent text-[10px] uppercase tracking-[0.35em] text-[#F7F0E3]/50">
              In Good Company · Preferred Partners
            </p>
          </div>
          <div className="mt-8 overflow-hidden border-y border-[#F7F0E3]/10 py-8">
            <div className="animate-marquee flex w-max items-center">
              {[0, 1].map((copy) => (
                <div key={copy} className="flex items-center">
                  {BRAND_STYLES.map((b) => (
                    <span key={`${copy}-${b.name}`} className="flex items-center">
                      <span
                        className={`whitespace-nowrap px-10 font-display text-4xl font-light italic text-[#F7F0E3]/70 sm:text-5xl ${b.cls}`}
                      >
                        {b.name}
                      </span>
                      <RuotaMark size={18} duration={12} />
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="mt-16 grid gap-12 border-t border-[#F7F0E3]/10 pt-14 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <RuotaMark size={40} duration={16} ballR={10} />
              <span className="font-display text-lg font-semibold tracking-[0.18em]">RUOTA</span>
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-[#F7F0E3]/60">
              A boutique travel advisory for the curious. Your journey is more than where you
              go, it's what you discover along the way.
            </p>
            <div className="mt-6 flex gap-3">
              <a
                data-testid="footer-social-instagram"
                href="https://www.instagram.com/jill__bauman/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F7F0E3]/20 transition-colors duration-300 hover:border-[#E2725B] hover:text-[#E2725B]"
              >
                <Instagram size={16} />
              </a>
              <a
                data-testid="footer-social-linkedin"
                href="https://www.linkedin.com/in/jillbauman/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F7F0E3]/20 transition-colors duration-300 hover:border-[#E2725B] hover:text-[#E2725B]"
              >
                <Linkedin size={16} />
              </a>
              <a
                data-testid="footer-social-email"
                href="mailto:jill@ruotatravel.com"
                aria-label="Email"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#F7F0E3]/20 transition-colors duration-300 hover:border-[#E2725B] hover:text-[#E2725B]"
              >
                <Mail size={16} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-[#E2725B]">Navigate</p>
            <div className="mt-5 flex flex-col gap-3.5">
              {NAV.map((l) => (
                <a
                  key={l.testid}
                  href={l.href}
                  data-testid={l.testid}
                  onClick={(e) => go(e, l.href)}
                  className="w-max text-sm text-[#F7F0E3]/70 transition-colors duration-300 hover:text-[#F7F0E3]"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-5">
            <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-[#E2725B]">
              Postcards from the road
            </p>
            <p className="mt-5 text-sm leading-relaxed text-[#F7F0E3]/60">
              Occasional letters on places that move us. No noise, ever.
            </p>
            <form onSubmit={subscribe} className="mt-5 flex items-center gap-3 border-b border-[#F7F0E3]/25 pb-3 transition-colors focus-within:border-[#E2725B]">
              <input
                data-testid="footer-newsletter-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full bg-transparent text-sm text-[#F7F0E3] placeholder-[#F7F0E3]/40 outline-none"
              />
              <button
                data-testid="footer-newsletter-submit"
                type="submit"
                disabled={sending}
                aria-label="Subscribe"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#168A91] transition-colors duration-300 hover:bg-[#E2725B] hover:text-[#0D1F23] disabled:opacity-50"
              >
                <ArrowRight size={15} />
              </button>
            </form>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-[#F7F0E3]/10 py-8">
          <p className="font-accent text-[10px] uppercase tracking-[0.3em] text-[#F7F0E3]/40">
            © 2026 Ruota Travel
          </p>
          <p className="font-display text-sm italic text-[#F7F0E3]/50">
            Life moves. Go with it.
          </p>
        </div>
      </div>
    </footer>
  );
};
